import { useState } from "react";

import { TFormValues } from "../types";

const formNameMap: { [key: string]: string } = {
  legal: "Legal Advice",
  protesting: "Protesting",
  eligible: "Eligible",
  guided: "Guided Search",
  court: "Court",
  ineligible: "Ineligible",
  priority: "Priority Callback",
};

export const useAnalytics = () => {
  const [formStartTime, setFormStartTime] = useState(new Date());
  const [currentForm, setCurrentForm] = useState("");

  // Set form start for any of the main form sections
  const formStarted = (currentStep: string) => {
    const formValue = currentStep.split("-")[0];
    const name = formNameMap[formValue];

    setFormStartTime(new Date());
    setCurrentForm(name);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "custom.form.start",
      action: "start",
      name,
    });
  };

  // Set when a step is completed on GA
  const stepCompleted = (
    stepName: string,
    stepNumber: string,
    formData: TFormValues | null
  ) => {
    window.dataLayer = window.dataLayer || [];
    const timeFromStartInSeconds = Math.floor(
      (new Date().getTime() - formStartTime.getTime()) / 1000
    );

    const stepData = getStepData(stepName, formData);

    window.dataLayer.push({
      event: "custom.form.step.complete",
      action: "step complete",
      name: currentForm,
      data: stepData,
      step: {
        name: stepName,
        number: stepNumber,
      },
      // Get seconds from start time
      timeFromStart: timeFromStartInSeconds,
    });
  };

  const submitForm = () => {
    const timeFromStartInSeconds = Math.floor(
      (new Date().getTime() - formStartTime.getTime()) / 1000
    );

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "custom.form.complete",
      action: "complete",
      name: currentForm,
      timeFromStart: timeFromStartInSeconds,
    });
  };

  // Set if a user is eligible on GA
  const setEligibility = (eligibility: "eligible" | "ineligible") => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "custom.form.eligibility",
      action: "eligibility",
      outcome: eligibility,
    });
  };

  return { formStarted, stepCompleted, submitForm, setEligibility };
};

const getStepData = (stepName: string, formData: TFormValues | null) => {
  if (!formData) return;

  switch (stepName) {
    // Resources by state - State selected
    case "guided-step-one":
    case "legal-step-two":
      return formData.location;
    // How would you describe your main concern? - Title of main concern selected
    case "guided-step-two":
    case "legal-step-five":
      return formData.concern;
    // What is the outcome you’re hoping for? - Outcome selected
    case "guided-step-three":
    case "legal-step-six":
      return formData.outcomes;
    // First nations person - Store selection
    case "legal-step-three":
      return formData.aboriginalOrTorresStrait;
    // Is there a deadline - Deadline entered
    case "eligible-step-three":
      return formData.timeSensitiveDate;
    // Is there another party involved - yes/no
    case "eligible-step-four":
      return formData.otherPartyName !== "" ? "yes" : "no";
    // Are you applying on behalf of someone else - yes/no
    case "eligible-step-five":
      return formData.applyingOnBehalf;
    default:
      return undefined;
  }
};
